import React, { ChangeEvent } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Typography,
  Box,
  Checkbox,
  IconButton,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import Checked from "../../assets/images/checkbox-checked.png";
import { RootState } from "../../store/rootReducer";
import { actions } from "../../slices/flow";
import Unchecked from "../../assets/images/checkbox-select-all.svg";
import LeftArrow from "../../assets/images/navigation-left.svg";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(1.75)} ${theme.spacing(4.5)}`,
    backgroundColor: theme.palette.background.default,
    width: "100%",
    boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.15)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    transition: "0.5s",
  },
  btn: {
    padding: "10px 20px !important",
    minWidth: "100px !important",
    boxShadow: "none !important",
    "& p": {
      fontWeight: "600 !important",
      lineHeight: "16px !important",
      fontSize: "14px !important",
      textTransform: "capitalize !important",
    },
  },
  checkbox: {
    width: 16,
    height: 16,
  },
}));

const EditWindow: React.FC<{
  onCancel: () => void;
  onNext: () => void;
  onBack: () => void;
  editMode: number;
}> = ({ onCancel, onNext, onBack, editMode }) => {
  const classes = useStyles();
  const newFlow = useSelector((state: RootState) => state.flow);
  const dispatch = useDispatch();
  const handleCancel = () => {
    onCancel();
  };
  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(actions.selectAll(event.target.checked));
  };
  const handleSetLoop = (checked: boolean) => {
    dispatch(actions.setAllowLoop(checked));
  };
  const selectedCount = () =>
    newFlow.dom_lists.filter((snapshot) => snapshot.checked).length;
  const isAllSelected = () =>
    newFlow.dom_lists.every((snapshot) => snapshot.checked);
  const isAnySelected = () =>
    newFlow.dom_lists.some((snapshot) => snapshot.checked);
  return (
    <Box className={classes.root}>
      {editMode === 1 && (
        <Box display="flex" gap={2}>
          <Tooltip title={isAllSelected() ? "Deselect All" : "Select All"}>
            <Checkbox
              classes={{ root: classes.checkbox }}
              icon={<img src={Unchecked} width="18" height="18" alt="icon" />}
              checkedIcon={
                <img src={Checked} width="18" height="18" alt="icon" />
              }
              onChange={handleSelectAll}
              checked={isAllSelected()}
              inputProps={{
                "aria-label": "Select All Checkbox",
              }}
            />
          </Tooltip>
          <Box>
            <Typography variant="subtitle2">
              EDIT FLOW: <b>ADD OR REMOVE SNAPSHOTS</b>
            </Typography>
            <Typography variant="h5" sx={{ marginTop: 0.25 }}>
              {selectedCount()} Selected
            </Typography>
          </Box>
        </Box>
      )}
      {editMode === 2 && (
        <Box display="flex" gap={2}>
          <IconButton
            sx={{ padding: 0.5 }}
            onClick={onBack}
            aria-label="Back To First Step"
          >
            <img src={LeftArrow} alt="left" width="18" height="18" />
          </IconButton>
          <Box>
            <Typography variant="subtitle2">
              EDIT FLOW: <b>EDIT VALUES</b>
            </Typography>
            {/* <Typography variant="h5" sx={{ marginTop: 0.25 }}>
              {selectedCount()} Selected
            </Typography> */}
          </Box>
        </Box>
      )}
      {editMode !== 0 && (
        <Box mr="auto" ml={3}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  color="secondary"
                  checked={!!newFlow.allow_loop}
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                    checked: boolean
                  ) => {
                    handleSetLoop(checked);
                  }}
                />
              }
              label="Allow loop"
            />
          </FormGroup>
        </Box>
      )}
      <Box display="flex" gap={1.25}>
        <Button
          variant="outlined"
          color="primary"
          className={classes.btn}
          onClick={handleCancel}
          aria-label="Cancel Edit Mode"
        >
          <Typography>Cancel</Typography>
        </Button>
        <Button
          variant="contained"
          color="secondary"
          className={classes.btn}
          onClick={onNext}
          disabled={!isAnySelected()}
          aria-label="Next Mode"
        >
          <Typography>{editMode === 1 ? "Next" : "Save Changes"}</Typography>
        </Button>
      </Box>
    </Box>
  );
};

EditWindow.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  editMode: PropTypes.oneOf([0, 1, 2]).isRequired,
};

export default EditWindow;
